import React from 'react'
import PropTypes from 'prop-types'
import { styled } from '@mui/material/styles'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { mikePalette } from '@mike/mike-shared-frontend/mike-shared-styles/mikeSharedTheme'
import Tooltip from '@mui/material/Tooltip'
import uniq from 'lodash/uniq'

const StyledTreeView = styled(SimpleTreeView)({
  height: 216,
  flexGrow: 1,
  maxWidth: 400
})

const ProjectsTree = ({
  onExpand,
  onSelect,
  projectTree,
  selectedIds,
  disabledIds,
  parentId,
  parentIdTooltip,
  tooltip,
  selectedItemTooltip
}) => {
  const allDisabledIds = uniq(
    [parentId].concat(selectedIds).concat(disabledIds)
  )

  const handleItemExpansionToggle = (event, itemId, isExpanded) => {
    event?.preventDefault()

    if (isExpanded && itemId !== 'project' && !selectedIds.includes(itemId)) {
      onExpand(itemId)
    }
  }

  const handleSelectedItemsChange = (event, itemId) => {
    event?.preventDefault()
    const selectedId = itemId && allDisabledIds.includes(itemId) ? null : itemId
    onSelect(selectedId ?? null)
  }

  const getTooltip = id => {
    if (parentId === id) return parentIdTooltip

    if (disabledIds.includes(id)) return tooltip

    if (selectedIds.includes(id)) return selectedItemTooltip

    return null
  }

  const renderTree = children => {
    return children?.map((child, index) => {
      const childrenNodes =
        projectTree[child.id]?.length > 0
          ? renderTree(projectTree[child.id])
          : [<div key={index} />]

      const tip = getTooltip(child.id)
      const disabled = tip !== null

      return (
        <TreeItem
          key={child.id}
          itemId={child.id}
          label={
            disabled ? (
              <Tooltip title={tip}>
                <span>{child.name}</span>
              </Tooltip>
            ) : (
              <div>{child.name}</div>
            )
          }
          sx={
            disabled
              ? {
                  color: mikePalette.primary.light
                }
              : {
                  color: mikePalette.primary.main,
                  '&:hover > .MuiTreeItem-content': {
                    backgroundColor: mikePalette.secondary.light
                  },
                  '&:focus > .MuiTreeItem-content, &.Mui-selected > .MuiTreeItem-content': {
                    backgroundColor: mikePalette.primary.main,
                    color: mikePalette.secondary.contrastText
                  },
                  '&:focus > .MuiTreeItem-content .MuiTreeItem-label, &:hover > .MuiTreeItem-content .MuiTreeItem-label, &.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
                    backgroundColor: 'transparent'
                  }
                }
          }
        >
          {childrenNodes}
        </TreeItem>
      )
    })
  }

  return (
    <StyledTreeView
      slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
      expandedItems={projectTree ? Object.keys(projectTree) : []}
      onItemExpansionToggle={handleItemExpansionToggle}
      onSelectedItemsChange={handleSelectedItemsChange}
    >
      {projectTree && projectTree.project && renderTree(projectTree.project)}
    </StyledTreeView>
  )
}

ProjectsTree.propTypes = {
  projectTree: PropTypes.object,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabledIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  parentId: PropTypes.string,
  parentIdTooltip: PropTypes.string.isRequired,
  selectedItemTooltip: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
  onExpand: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
}

export default ProjectsTree
